import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import CloseIcon from '@mui/icons-material/Close';
import { QModal, QModalTitle, QModalContent, QModalActions, QButton, QTypography, QSelect } from '@kw/quokka-ui';
import { Box } from '@mui/system';

const Title = ({ toggle, appName }) => (
  <QModalTitle onClose={toggle} closeIcon={<CloseIcon />}>
    <QTypography variant="h3Medium">PDF configuration for {appName}</QTypography>
  </QModalTitle>
);

const Body = ({
  riskOptions,
  riskSelectValues,
  handleRiskChange,
  tabOptions,
  tabSelectValues,
  handleTabChange,
  evidenceOptions,
  evidenceSelectValue,
  handleEvidenceChange,
  overviewRuleCategoryOptions,
  overviewRuleCategorySelectValues,
  handleOverviewRuleCategoryChange,
  behavioralRuleCategoryOptions,
  behavioralRuleCategorySelectValues,
  handleBehavioralRuleCategoryChange,
  manuallyPassedRulesOptions,
  manuallyPassedRulesSelectValue,
  handleManuallyPassedRulesChange,
}) => (
  <QModalContent>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ width: '100%', mt: 1 }}>
        <QSelect
          size="large"
          options={riskOptions}
          value={riskSelectValues}
          onChange={handleRiskChange}
          multiple
          label="Risk Levels"
          formControlProps={{ sx: { width: '100%' } }}
          selectIconProps={{ fontSize: 'medium' }}
        />
      </Box>
      <Box sx={{ width: '100%', mt: 1 }}>
        <QSelect
          size="large"
          options={tabOptions}
          value={tabSelectValues}
          onChange={handleTabChange}
          multiple
          label="Tabs"
          formControlProps={{ sx: { width: '100%' } }}
          selectIconProps={{ fontSize: 'medium' }}
        />
      </Box>
      <Box sx={{ width: '100%', mt: 1 }}>
        <QSelect
          size="large"
          options={evidenceOptions}
          value={evidenceSelectValue}
          onChange={handleEvidenceChange}
          label="Evidence"
          formControlProps={{ sx: { width: '100%' } }}
          selectIconProps={{ fontSize: 'medium' }}
        />
      </Box>
      <Box sx={{ width: '100%', mt: 1 }}>
        <QSelect
          size="large"
          multiple
          options={overviewRuleCategoryOptions}
          value={overviewRuleCategorySelectValues}
          onChange={handleOverviewRuleCategoryChange}
          label="Overview Rule Categories"
          formControlProps={{ sx: { width: '100%' } }}
          selectIconProps={{ fontSize: 'medium' }}
          className="z-index-10"
        />
      </Box>
      <Box sx={{ width: '100%', mt: 1 }}>
        <QSelect
          size="large"
          multiple
          options={behavioralRuleCategoryOptions}
          value={behavioralRuleCategorySelectValues}
          onChange={handleBehavioralRuleCategoryChange}
          label="Behaviors Rule Categories"
          formControlProps={{ sx: { width: '100%' } }}
          selectIconProps={{ fontSize: 'medium' }}
          className="z-index-10"
        />
      </Box>
      <Box>
        <QSelect
          size="large"
          options={manuallyPassedRulesOptions}
          value={manuallyPassedRulesSelectValue}
          onChange={handleManuallyPassedRulesChange}
          label="Manually Passed Rules"
          formControlProps={{ sx: { width: '100%' } }}
          selectIconProps={{ fontSize: 'medium' }}
        />
      </Box>
      <Box>
        <QTypography variant="caption" color="textSecondary">
          Note: For the best viewing and rendering performances, use a Chromium-based browser such as Google Chrome or Microsoft Edge.
        </QTypography>
      </Box>
    </Box>
  </QModalContent>
);

const Footer = ({ toggle, loadReport }) => (
  <QModalActions>
    <QButton variant="light" color="secondary" onClick={toggle}>
      <QTypography variant="bodyRegular">Cancel</QTypography>
    </QButton>
    <QButton variant="filled" color="primary" onClick={() => loadReport()}>
      <QTypography variant="bodyRegular" color="#FFFFFF">
        Generate PDF Report
      </QTypography>
    </QButton>
  </QModalActions>
);
export const PdfViewOptionsModal = ({
  isOpen,
  toggle,
  appName,
  analysisId,
  overviewRuleCategoryOptions,
  behavioralRuleCategoryOptions,
  os,
}: {
  isOpen: boolean;
  toggle: () => void;
  appName: string;
  analysisId: string;
  overviewRuleCategoryOptions: { value: string; label: string }[];
  behavioralRuleCategoryOptions: { value: string; label: string }[];
  os: string;
}) => {
  const navigate = useNavigate();
  const { iosSbom, niap20 } = useFlags();

  const platform = os;
  const shouldShowSbomOption = platform !== 'ios' || iosSbom;

  // In the future, these access variables can be set based on user permissions configured in the system
  const sbomAccess = true;
  const maliciousAccess = true;
  const niapAccess = true;
  const gdprAccess = true;
  const owaspAccess = true;

  const [riskSelectValues, setRiskSelectValues] = React.useState([0, 1, 2, 3]);
  const [tabSelectValues, setTabSelectValues] = React.useState([
    'overview',
    'behavior',
    'network_info',
    ...(shouldShowSbomOption ? ['sbom'] : []),
    'malicious_report',
    'owasp_masvs',
    'owasp_2024',
    'owasp_2016',
    'gdpr',
    'niap-1.3',
    'niap-1.4',
    ...(niap20 ? ['niap-2.0'] : []),
  ]);
  const [evidenceSelectValue, setEvidenceSelectValue] = React.useState('partial');
  const [overviewRuleCategorySelectValues, setOverviewRuleCategorySelectValues] = React.useState<string[]>(['all']);
  const [behavioralRuleCategorySelectValues, setBehavioralRuleCategorySelectValues] = React.useState<string[]>(['all']);
  const [manuallyPassedRulesSelectValue, setManuallyPassedRulesSelectValue] = React.useState('show');

  const riskOptions = [
    { value: 0, label: 'Info' },
    { value: 1, label: 'Note' },
    { value: 2, label: 'Warning' },
    { value: 3, label: 'Error' },
  ];

  const tabOptions = [
    { value: 'overview', label: 'Overview' },
    { value: 'behavior', label: 'Behavior' },
    { value: 'network_info', label: 'Network Info' },
    shouldShowSbomOption && { value: 'sbom', label: 'SBOM' },
    maliciousAccess && { value: 'malicious_report', label: 'Malicious Report' },
    owaspAccess && { value: 'owasp_masvs', label: 'OWASP MASVS' },
    owaspAccess && { value: 'owasp_2024', label: 'OWASP Mobile Top 10 2024' },
    owaspAccess && { value: 'owasp_2016', label: 'OWASP Mobile Top 10 2016' },
    gdprAccess && { value: 'gdpr', label: 'GDPR' },
    niapAccess && { value: 'niap-1.3', label: 'NIAP 1.3' },
    niapAccess && { value: 'niap-1.4', label: 'NIAP 1.4' },
    niapAccess && niap20 && { value: 'niap-2.0', label: 'NIAP 2.0' },
  ].filter(Boolean);

  const evidenceOptions = [
    {
      value: 'all',
      label: 'All',
    },
    {
      value: 'partial',
      label: 'Partial',
    },
    {
      value: 'none',
      label: 'None',
    },
  ];

  const manuallyPassedRulesOptions = [
    {
      value: 'show',
      label: 'Show',
    },
    {
      value: 'hide',
      label: 'Hide',
    },
  ];

  const handleRiskChange = event => {
    const {
      target: { value },
    } = event;
    setRiskSelectValues(value.length ? value : [value]);
  };

  const handleTabChange = event => {
    const {
      target: { value },
    } = event;
    setTabSelectValues(value.length ? value : [value]);
  };
  const handleEvidenceChange = (event, option) => {
    const newOption = evidenceOptions.find(item => item.value === option.props.value);
    setEvidenceSelectValue(newOption.value);
  };

  const handleManuallyPassedRulesChange = (event, option) => {
    const newOption = manuallyPassedRulesOptions.find(item => item.value === option.props.value);
    setManuallyPassedRulesSelectValue(newOption.value);
  };

  const handleOverviewRuleCategoryChange = event => {
    const {
      target: { value },
    } = event;

    const valueArray = Array.isArray(value) ? value : [value];

    if (overviewRuleCategorySelectValues.includes('all')) {
      if (valueArray.length > overviewRuleCategorySelectValues.length) {
        setOverviewRuleCategorySelectValues(valueArray.filter(v => v !== 'all'));
      }
    } else if (valueArray.includes('all')) {
      setOverviewRuleCategorySelectValues(['all']);
    } else {
      setOverviewRuleCategorySelectValues(valueArray);
    }
  };

  const handleBehavioralRuleCategoryChange = event => {
    const {
      target: { value },
    } = event;

    const valueArray = Array.isArray(value) ? value : [value];

    if (behavioralRuleCategorySelectValues.includes('all')) {
      if (valueArray.length > behavioralRuleCategorySelectValues.length) {
        setBehavioralRuleCategorySelectValues(valueArray.filter(v => v !== 'all'));
      }
    } else if (valueArray.includes('all')) {
      setBehavioralRuleCategorySelectValues(['all']);
    } else {
      setBehavioralRuleCategorySelectValues(valueArray);
    }
  };

  // const generateReport = async ({ farmAppUuid, analyzedAppId, path }) => {
  //   try {
  //     await axios.post(`pdf-request`, [{ farmAppUuid, analyzedAppId, path }]);
  //     toastrHelper.showSuccessToast('Successfully requesting report generation', 'PDF Report Generation', true);
  //   } catch (err) {
  //     toastrHelper.showErrorToast('Error requesting report generation', 'PDF Report Generation', true);
  //   }
  // };

  const loadReport = async () => {
    const searchParams = new URLSearchParams();
    searchParams.append('risks', riskSelectValues.join(','));
    searchParams.append('tabs', tabSelectValues.join(','));
    searchParams.append('evidence', evidenceSelectValue);
    searchParams.append(
      'overview_rule_categories',
      (overviewRuleCategorySelectValues.includes('all')
        ? Object.values(overviewRuleCategoryOptions)
            .map(entry => entry.value)
            .slice(1)
        : overviewRuleCategorySelectValues
      ).join(','),
    );
    searchParams.append(
      'behavioral_rule_categories',
      (behavioralRuleCategorySelectValues.includes('all')
        ? Object.values(behavioralRuleCategoryOptions)
            .map(entry => entry.value)
            .slice(1)
        : behavioralRuleCategorySelectValues
      ).join(','),
    );
    searchParams.append('show_manually_passed_items', (manuallyPassedRulesSelectValue === 'show').toString());

    const pathname = `/new-report-pdf-view/${analysisId}`;
    const search = searchParams.toString();

    // Navigate to the PDF view
    navigate(pathname + '?' + search);

    toggle();
  };

  const tabAccessMapping = useMemo(
    () => ({
      sbom: !!sbomAccess,
      'malicious_report': !!maliciousAccess,
      gdpr: !!gdprAccess,
      'owasp_masvs': !!owaspAccess,
      'owasp_2024': !!owaspAccess,
      'owasp_2016': !!owaspAccess,
      'niap_1.3': !!niapAccess,
      'niap_1.4': !!niapAccess,
      'niap_2.0': !!niapAccess,
    }),
    [sbomAccess, maliciousAccess, niapAccess, gdprAccess, owaspAccess],
  );

  useEffect(() => {
    setTabSelectValues(curr => curr.filter(tab => tabAccessMapping[tab] !== false));
  }, [tabAccessMapping]);

  return (
    <QModal
      open={isOpen}
      isOpen={isOpen}
      toggle={toggle}
      title={<Title toggle={toggle} appName={appName} />}
      content={
        <Body
          riskOptions={riskOptions}
          riskSelectValues={riskSelectValues}
          handleRiskChange={handleRiskChange}
          tabOptions={tabOptions}
          tabSelectValues={tabSelectValues}
          handleTabChange={handleTabChange}
          evidenceOptions={evidenceOptions}
          evidenceSelectValue={evidenceSelectValue}
          handleEvidenceChange={handleEvidenceChange}
          overviewRuleCategoryOptions={overviewRuleCategoryOptions}
          overviewRuleCategorySelectValues={overviewRuleCategorySelectValues}
          handleOverviewRuleCategoryChange={handleOverviewRuleCategoryChange}
          behavioralRuleCategoryOptions={behavioralRuleCategoryOptions}
          behavioralRuleCategorySelectValues={behavioralRuleCategorySelectValues}
          handleBehavioralRuleCategoryChange={handleBehavioralRuleCategoryChange}
          manuallyPassedRulesOptions={manuallyPassedRulesOptions}
          manuallyPassedRulesSelectValue={manuallyPassedRulesSelectValue}
          handleManuallyPassedRulesChange={handleManuallyPassedRulesChange}
        />
      }
      actions={<Footer loadReport={loadReport} toggle={toggle} />}
      maxWidth="md"
      fullWidth="md"
    />
  );
};
